import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import usePriceList, { usePriceListForm } from '@hooks/usePriceList'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'DuplicatePriceListModal'

type ModalCallbacks = {
  closeModal: () => void,
  duplicatePriceList: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const DuplicatePriceListModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, duplicatePriceList } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const priceList = digObject(modalPayload, 'priceList', {})

  const {
    callbacks: {
      duplicatePriceList: duplicateFn,
    },
    creating,
  } = usePriceList(priceList)

  const priceListFormPayload = usePriceListForm(
    { ...priceList, name: `${priceList.name} - Copy` },
    { validateOn: 'change' },
  )

  const {
    entityState,
    entityState: {
      name,
    },
    errors,
    handlers,
    saveEnabled,
  } = priceListFormPayload

  // To be used with handleCallbackAction
  const duplicatePriceListPayload = {
    callbacks: {
      action: duplicateFn,
      afterAction: closeModal,
    },
    shouldRedirect: true,
    entityParams: entityState,
    toastText: 'Price List Duplicated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Duplicate" titleSecondLine="Price List" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Name" errorMessage={errors.name}>
            <input name="name" type="text" value={name} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          loadingBubbleColor="white"
          onClick={() => duplicatePriceList(duplicatePriceListPayload)}
          size="large"
        >
          {saveEnabled ? 'Duplicate Price List' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <DuplicatePriceListModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
